/**
 * One drag's latest width, coalesced into at most one pending browser frame.
 * No timers run at rest. Returning to the displayed width cancels pending work;
 * flush preserves the final pointer position when pointer-up beats the frame.
 */
export function createSidebarWidthFrame(options: {
  readonly initialWidth: number;
  readonly applyWidth: (width: number) => void;
  readonly requestFrame: (callback: () => void) => number;
  readonly cancelFrame: (id: number) => void;
}) {
  let displayedWidth = options.initialWidth;
  let pendingWidth = displayedWidth;
  let frame: number | null = null;
  let active = true;

  function cancelPendingFrame() {
    if (frame === null) return;
    options.cancelFrame(frame);
    frame = null;
  }

  function applyPendingWidth() {
    frame = null;
    if (!active || pendingWidth === displayedWidth) return;
    displayedWidth = pendingWidth;
    options.applyWidth(displayedWidth);
  }

  return {
    move(width: number) {
      if (!active) return;
      pendingWidth = width;
      if (pendingWidth === displayedWidth) {
        cancelPendingFrame();
      } else if (frame === null) {
        frame = options.requestFrame(applyPendingWidth);
      }
    },
    flush() {
      cancelPendingFrame();
      applyPendingWidth();
      return displayedWidth;
    },
    cancel() {
      active = false;
      cancelPendingFrame();
    },
  };
}

export type SidebarWidthFrame = ReturnType<typeof createSidebarWidthFrame>;
